import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Spinner, Box, Text, Tabs, Badge } from '@radix-ui/themes';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ProjectType } from '../../../types/ScientificModule';
import { routes } from '../../../routes';
import TextFieldComponent from '../../../components/inputs/Textfield/TextfieldComponent';

import { CreateProjectFormValidation } from './CreateProjectFormValidation';
import { dataURLtoFile, getFile, getFileExt } from '../../../utils/fileUtils';
import { createProject, updateProject } from '../../../services/scientific/project';
import DateFieldComponent from '../../../components/inputs/Datefield/DatefieldComponent';
import { DragAndDropFileUpload } from '../../../components/inputs/FileUpload/DragAndDropFileUpload';
import { useState } from 'react';
import { useGetProjectTypesQuery } from '../../../hooks/scientific/useGetProjectTypesQuery';
import SelectComponent from '../../../components/inputs/Select/SelectComponent';
import TextAreaFieldComponent from '../../../components/inputs/TextAreafield/TextAreafieldComponent';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { toDateOnly } from '../../../utils/dateFormatter';
type ActiveSegmentTab = 'Form' | 'Documents';

export const UpdateProject = () => {
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: updateProject, mutationKey: 'updateProject' });
  const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('Form');
  const { data: projectTypes, isLoading: isLoadingProjectTypes, isError: isErrorProjectTypes, refetch: refreshProjectTypes } = useGetProjectTypesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: entities, isLoading: isLoadingEntities, isError: isErrorEntities, refetch: refreshEntities } = useGetProjectTypesQuery({ PageNumber: 1, PageSize: 400 });
  const projectFormData = useSelector((state: RootState) => state.projects);

  const defaultValues: ProjectType = projectFormData;

  const { control, handleSubmit, formState: { errors }, trigger } = useForm<ProjectType>({
    resolver: yupResolver(CreateProjectFormValidation),
    defaultValues: {
      ...defaultValues,
      principalDocBase64File: defaultValues?.principalDocBase64 ? dataURLtoFile(defaultValues.principalDocBase64, getFileExt(defaultValues.principalDocBase64, "principal_doc.pdf")) : undefined,
      documents: defaultValues?.documents?.map((d, index) => {
        if (d.docBase64) {
          const file = dataURLtoFile(d.docBase64, `doc_${index}.pdf`);
          return { ...d, file, };
        }
        return d;
      }) ?? []
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      data = {
        ...data,
        startdate: toDateOnly(new Date(data.startdate)),
        enddate: data.enddate ? toDateOnly(new Date(data.enddate)) : undefined

      }
      const { principalDocBase64File, ...rest } = data;
      const fileBase64 = principalDocBase64File
        ? await getFile(principalDocBase64File)
        : '';
      const payload = { ...rest, principalDocBase64: fileBase64 };
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success('Projeto criado!');
        navigate(routes.projects);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    navigate(routes.projects);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });
  const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
    if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
    trigger();
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate: string) => navigateAndValidate(tabToNavigate as ActiveSegmentTab)}>
        <Tabs.List>
          <Tabs.Trigger value="Form">
            Dados Principais <Badge color="red">{Object.keys(errors)?.length}</Badge>
          </Tabs.Trigger>
          <Tabs.Trigger value="Documents">Documentos</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="Form">
          <Card size='1'>
            <Flex direction='column' gap='4'>
              <TextAreaFieldComponent name="title" label="Título" control={control} errors={errors} fieldProps={{ placeholder: 'Título' }} />
              <TextAreaFieldComponent name="description" label="Descrição" control={control} errors={errors} fieldProps={{ placeholder: 'Descrição' }} required={true}/>
              <TextAreaFieldComponent name="summary" label="Sumário" control={control} errors={errors} fieldProps={{ placeholder: 'Sumário' }} />
              <TextAreaFieldComponent name="targetAudience" label="Público-Alvo" control={control} errors={errors} fieldProps={{ placeholder: 'Público-Alvo' }} />
              <TextAreaFieldComponent name="linesOfResearch" label="Linhas de Pesquisa" control={control} errors={errors} fieldProps={{ placeholder: 'Linhas de Pesquisa' }} />
                         
              <SelectComponent
                name="idProjectType"
                label="Tipo de projecto"
                control={control}
                errors={errors}
                options={projectTypes?.items.map((projectType) => ({
                  value: projectType.id ?? "",
                  label: projectType.description ?? "",
                }))}
                itemsLoading={isLoadingProjectTypes}
                itemsError={isErrorProjectTypes}
                required={true}
              />
               <TextAreaFieldComponent name="expectedResults" label="Resultados Esperados" control={control} errors={errors} fieldProps={{ placeholder: 'Resultados Esperados' }} />
              <TextAreaFieldComponent name="finalConsiderations" label="Considerações Finais" control={control} errors={errors} fieldProps={{ placeholder: 'Considerações Finais' }} />
                       
              <Flex gap='4'>
                <DateFieldComponent name="startdate" label="Data de Início" control={control} errors={errors} style={{ width: '100%' }} required={true} />
                <DateFieldComponent name="enddate" label="Data de Fim" control={control} errors={errors} style={{ width: '100%' }} required={true} />
              </Flex>
              <Flex gap='4'>
                <SelectComponent
                  name="idEntity"
                  label="Entitidade"
                  control={control}
                  errors={errors}
                  options={entities?.items.map((entity) => ({
                    value: entity.id ?? "",
                    label: entity.description ?? "",
                  }))}
                  style={{ width: '100%' }}
                  itemsLoading={isLoadingEntities}
                  itemsError={isErrorEntities}
                  required={true}
                />
                <TextFieldComponent name="entityName" label="Entidade" control={control} errors={errors} fieldProps={{ placeholder: 'Nome da Entidade' }} style={{ width: '100%' }} />
              </Flex>
              <TextFieldComponent name="moneyInvested" label="Investimento" control={control} errors={errors} fieldProps={{ placeholder: 'Montante' }} />
              <TextAreaFieldComponent name="objectives" label="Objetivos" control={control} errors={errors} fieldProps={{ placeholder: 'Objetivos do projeto' }} required={true} />

              <Box>
                <Text as="label" size="2"><span>Documento Principal <span style={{ color: 'red' }}>*</span></span></Text>
                <Controller
                  name="principalDocBase64File"
                  control={control}
                  render={({ field }) => (
                    <DragAndDropFileUpload
                      onFileDrop={(file) => {
                        field.onChange(file);
                      }}
                      label={`Arraste ou selecione o arquivo`}
                      accept="*"
                      selectedFile={field.value}
                    />
                  )}
                />
                {errors.principalDocBase64File && (
                  <Text color="red" size="1">{`${errors.principalDocBase64File.message}`}</Text>
                )}
              </Box>

              <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
                  <ChevronLeftIcon /> voltar
                </Button>
                <Button type="submit" disabled={mutation.isLoading} variant='surface'>
                  {mutation.isLoading ? <Spinner loading><EnterIcon /></Spinner> : <EnterIcon />}
                  Registar
                </Button>
              </Flex>
            </Flex>
          </Card>
        </Tabs.Content>
        <Tabs.Content value="Documents">
          <Card className="p-4">
            <Flex direction="column" gap="3">
              <Text as="label">Documentos</Text>
              {fields.map((f, index) => (
                <Box
                  key={index}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    marginBottom: '10px',
                    position: 'relative',
                  }}
                >
                  <Controller
                    name={`documents.${index}.file` as const}
                    control={control}
                    render={({ field }) => (
                      <DragAndDropFileUpload
                        onFileDrop={(file) => {
                          field.onChange(file);
                        }}
                        accept="*"
                        selectedFile={field.value}
                        remove={() => remove(index)}
                      />
                    )}
                  />
                  {errors.documents?.[index]?.file && (
                    <Text color="red" size="1" style={{ marginTop: '5px' }}>
                      {`${errors.documents[index]?.message}`}
                    </Text>
                  )}
                </Box>
              ))}
              <Button type="button" variant="surface" color="blue" onClick={() => append(
                {
                  file: null
                }
              )}>
                Adicionar Documento
              </Button>
            </Flex>
          </Card>
        </Tabs.Content>
      </Tabs.Root>
    </form>
  );
};
